import type { JSX, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { VueDAigle } from '../../types.js';
import { LienConsole, NonComptees } from './composants.js';
import commun from './commun.module.css';
import { useDroitsVue } from './droits.js';
import { useFormats } from './formats.js';
import styles from './Indicateurs.module.css';

/**
 * Les six tuiles du haut de page. Les variations viennent des historiques de
 * l'API (le premier point de la période contre le dernier) : sans deux points,
 * pas de variation — rien n'est inventé.
 */
export function Indicateurs({ vue }: { vue: VueDAigle }): JSX.Element {
  const { t } = useTranslation('console');
  const f = useFormats();
  const { peut } = useDroitsVue(vue);
  const { indicateurs } = vue;

  const mrr = vue.revenus.historiqueMrr;
  const parc = vue.parc.historique;
  const mrrDepart = mrr.length >= 2 ? mrr[0]! : null;
  const parcDepart = parc.length >= 2 ? parc[0]! : null;

  /** Une variation : texte, sens, et le mois de référence. */
  function variation(mois: string, texte: string, delta: number, colorie = true): JSX.Element {
    return (
      <span className={styles.variation}>
        <span
          className={colorie ? (delta > 0 ? commun.hausse : delta < 0 ? commun.baisse : '') : ''}
        >
          {texte}
        </span>
        <span>{t('vueDAigle.indicateurs.variation', { mois: f.moisLong(mois) })}</span>
      </span>
    );
  }

  const variationMrr = ((): ReactNode => {
    if (!mrrDepart) return null;
    const delta = indicateurs.mrrCentimes - mrrDepart.mrrCentimes;
    const texte =
      mrrDepart.mrrCentimes > 0
        ? `${f.pourcentSigne(delta / mrrDepart.mrrCentimes)} · ${f.eurosSigne(delta)}`
        : f.eurosSigne(delta);
    return variation(mrrDepart.mois, texte, delta);
  })();

  const variationArr = ((): ReactNode => {
    if (!mrrDepart || mrrDepart.mrrCentimes <= 0) return null;
    const delta = indicateurs.mrrCentimes - mrrDepart.mrrCentimes;
    return variation(mrrDepart.mois, f.pourcentSigne(delta / mrrDepart.mrrCentimes), delta);
  })();

  const variationProduction = parcDepart
    ? variation(
        parcDepart.mois,
        f.nombreSigne(indicateurs.galeriesEnProduction - parcDepart.production),
        indicateurs.galeriesEnProduction - parcDepart.production,
      )
    : null;

  // Un essai qui baisse n'est ni bon ni mauvais : pas de couleur.
  const variationEssai = parcDepart
    ? variation(
        parcDepart.mois,
        f.nombreSigne(indicateurs.galeriesEnEssai - parcDepart.essai),
        indicateurs.galeriesEnEssai - parcDepart.essai,
        false,
      )
    : null;

  // Vue admin seulement : le détail par galerie y est.
  const finsEssai = (vue.parc.detail ?? [])
    .filter((galerie) => galerie.etat === 'essai' && galerie.finEssaiLe !== null)
    .map((galerie) => galerie.finEssaiLe as string)
    .sort();
  const prochaineFinEssai = finsEssai[0];

  const marge = indicateurs.margeMoisCentimes;
  const peutSaisirCouts = peut('console:manage-costs');
  const tauxMarge =
    marge !== null && indicateurs.mrrCentimes > 0 ? marge / indicateurs.mrrCentimes : null;

  return (
    <dl className={styles.tuiles} aria-label={t('vueDAigle.indicateurs.aria')}>
      <div className={commun.tuile}>
        <dt>{t('vueDAigle.indicateurs.mrr')}</dt>
        <dd className={`${commun.valeur} ${commun.valeurCle}`}>
          {f.euros(indicateurs.mrrCentimes)}
        </dd>
        {variationMrr ? <dd>{variationMrr}</dd> : null}
        <dd className={commun.aide}>{t('vueDAigle.indicateurs.mrrAide')}</dd>
        {indicateurs.abonnementsSansMontant > 0 ? (
          <dd>
            <NonComptees nombre={indicateurs.abonnementsSansMontant} />
          </dd>
        ) : null}
      </div>

      <div className={commun.tuile}>
        <dt>{t('vueDAigle.indicateurs.arr')}</dt>
        <dd className={commun.valeur}>{f.euros(indicateurs.arrCentimes)}</dd>
        {variationArr ? <dd>{variationArr}</dd> : null}
        <dd className={commun.aide}>{t('vueDAigle.indicateurs.arrAide')}</dd>
      </div>

      <div className={commun.tuile}>
        <dt>{t('vueDAigle.indicateurs.production')}</dt>
        <dd className={commun.valeur}>{f.nombre(indicateurs.galeriesEnProduction)}</dd>
        {variationProduction ? <dd>{variationProduction}</dd> : null}
        <dd className={commun.aide}>{t('vueDAigle.indicateurs.productionAide')}</dd>
      </div>

      <div className={commun.tuile}>
        <dt>{t('vueDAigle.indicateurs.essai')}</dt>
        <dd className={commun.valeur}>{f.nombre(indicateurs.galeriesEnEssai)}</dd>
        {variationEssai ? <dd>{variationEssai}</dd> : null}
        <dd className={commun.aide}>
          {prochaineFinEssai
            ? t('vueDAigle.indicateurs.prochaineFinEssai', { date: f.jourMois(prochaineFinEssai) })
            : t('vueDAigle.indicateurs.essaiAide')}
        </dd>
      </div>

      <div className={commun.tuile}>
        <dt>{t('vueDAigle.indicateurs.marge')}</dt>
        {marge === null ? (
          <dd className={`${commun.valeur} ${commun.valeurTexte}`}>
            {peutSaisirCouts ? (
              <LienConsole vers="/finances?onglet=couts">
                {t('vueDAigle.indicateurs.saisirCouts')}
              </LienConsole>
            ) : (
              t('vueDAigle.couts.nonSaisis')
            )}
          </dd>
        ) : (
          <dd className={commun.valeur}>
            {f.euros(marge)}
            {tauxMarge !== null ? <small>{f.pourcent(tauxMarge)}</small> : null}
          </dd>
        )}
        <dd className={commun.aide}>{t('vueDAigle.indicateurs.margeAide')}</dd>
      </div>

      <div className={commun.tuile}>
        <dt>{t('vueDAigle.indicateurs.utilisateurs')}</dt>
        <dd className={commun.valeur}>{f.nombre(indicateurs.utilisateursActifs30j)}</dd>
        <dd className={commun.aide}>{t('vueDAigle.indicateurs.utilisateursAide')}</dd>
      </div>
    </dl>
  );
}
